import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, Clock, AlertCircle, Euro, FileText, RefreshCw, Download, Mail, Loader2 } from 'lucide-react';
import api from '../../../../../api.js';
import EmailActionsPanel from '../../../../common/EmailActionsPanel.jsx';
import { useOrgCurrency } from '../../../../../hooks/useOrgCurrency.js';

/**
 * Insurance contract types with French labels
 */
const CONTRACT_TYPES = {
  main: { label: 'Contrat Principal', color: 'blue' },
  bris_de_machine: { label: 'Bris de Machine', color: 'purple' },
  garantie_panne: { label: 'Garantie Panne', color: 'indigo' },
  protection_juridique: { label: 'Protection Juridique', color: 'teal' },
  other: { label: 'Autre', color: 'gray' },
};

/**
 * StepExitInsurance - Insurance termination tracking step
 * Tracks termination status for each linked insurance contract
 */
function StepExitInsurance({ formData, updateFormData, vehicle, exitId }) {
  // Devise de l'organisation du vehicule - un seul outil monetaire.
  const { money, symbolOf } = useOrgCurrency();
  const currencySymbol = symbolOf(vehicle);
  const [vehicleInsurances, setVehicleInsurances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingEmail, setDownloadingEmail] = useState(false);

  // Direct download of insurance termination email
  const handleDownloadInsuranceEmail = async () => {
    if (!exitId) {
      setError('Exit ID requis pour generer l\'email');
      return;
    }
    setDownloadingEmail(true);
    setError('');
    try {
      await api.vehicleExit.downloadInsuranceEmail(exitId);
      // Mark as sent after download
      await api.vehicleExit.markInsuranceEmailSent(exitId);
      // Update all links to show termination requested
      const links = formData.insurance_links || [];
      const updatedLinks = links.map(link => ({ ...link, termination_requested: true }));
      updateFormData('insurance_links', updatedLinks);
    } catch (err) {
      setError(err.message || 'Erreur lors de la generation de l\'email');
    } finally {
      setDownloadingEmail(false);
    }
  };

  // Load vehicle insurance assignments
  useEffect(() => {
    const loadInsurances = async () => {
      if (!vehicle?.id) {
        setVehicleInsurances([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.vehicleInsurance.getByVehicle(vehicle.id);
        const insurances = (response.data || []).filter(vi => !vi.removed_at);

        // Fetch contract details for each insurance
        const enrichedInsurances = [];
        for (const ins of insurances) {
          let contractName = `Contrat #${ins.id}`;
          let policyNumber = '';
          let insurerName = '';

          if (ins.contract_id) {
            try {
              const contractRes = await api.insurance.getById(ins.contract_id);
              if (contractRes.data) {
                contractName = contractRes.data.insurer_name || contractRes.data.company_name || contractName;
                policyNumber = contractRes.data.policy_number || '';
                insurerName = contractRes.data.insurer_name || '';
              }
            } catch (e) {
              console.warn('Could not fetch contract details:', e);
            }
          }

          enrichedInsurances.push({
            ...ins,
            contractName,
            policyNumber,
            insurerName
          });
        }

        // Initialize insurance links in formData if not present
        if (!formData.insurance_links || formData.insurance_links.length === 0) {
          const links = enrichedInsurances.map((ins) => ({
            id: `link_${ins.id}`,
            vehicle_insurance_id: ins.id,
            contract_label: ins.contract_type || 'main',
            contract_name: ins.contractName,
            policy_number: ins.policyNumber,
            termination_requested: false,
            termination_confirmed: false,
            termination_confirmed_at: null,
            refund_requested: false,
            refund_received: false,
            refund_amount_eur: null,
            claims_history_requested: formData.insurance_mode === 'individual',
            claims_history_received: false,
            notes: '',
          }));
          updateFormData('insurance_links', links);
        }

        setVehicleInsurances(enrichedInsurances);
        setError('');
      } catch (err) {
        console.error('Error loading vehicle insurances:', err);
        setError('Erreur lors du chargement des assurances');
      } finally {
        setLoading(false);
      }
    };

    loadInsurances();
  }, [vehicle?.id, formData.insurance_mode]);

  // Update a specific insurance link
  const updateInsuranceLink = (linkId, field, value) => {
    const links = formData.insurance_links || [];
    const updatedLinks = links.map(link => {
      if (link.id === linkId) {
        const updated = { ...link, [field]: value };
        // Auto-set timestamp when confirming termination
        if (field === 'termination_confirmed' && value) {
          updated.termination_confirmed_at = new Date().toISOString();
        }
        return updated;
      }
      return link;
    });
    updateFormData('insurance_links', updatedLinks);
  };

  // Calculate completion stats
  const insuranceLinks = formData.insurance_links || [];
  const totalContracts = insuranceLinks.length;
  const confirmedCount = insuranceLinks.filter(l => l.termination_confirmed).length;
  const refundReceivedCount = insuranceLinks.filter(l => l.refund_received).length;
  const totalRefundAmount = insuranceLinks
    .filter(l => l.refund_received && l.refund_amount_eur)
    .reduce((sum, l) => sum + parseFloat(l.refund_amount_eur), 0);

  // All confirmations received?
  const allConfirmed = totalContracts > 0 && confirmedCount === totalContracts;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
        <span className="ml-2 text-gray-600">Chargement des assurances...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">Suivi Resiliation Assurance</h3>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Insurance Mode Banner */}
      <div className={`border rounded-lg p-4 ${
        formData.insurance_mode === 'fleet'
          ? 'bg-blue-50 border-blue-200'
          : 'bg-purple-50 border-purple-200'
      }`}>
        <div className="flex items-center gap-3">
          <Shield className={`w-6 h-6 ${
            formData.insurance_mode === 'fleet' ? 'text-blue-600' : 'text-purple-600'
          }`} />
          <div>
            <h4 className={`font-medium ${
              formData.insurance_mode === 'fleet' ? 'text-blue-900' : 'text-purple-900'
            }`}>
              {formData.insurance_mode === 'fleet' ? 'Contrat Flotte' : 'Contrat Individuel'}
            </h4>
            <p className={`text-sm ${
              formData.insurance_mode === 'fleet' ? 'text-blue-700' : 'text-purple-700'
            }`}>
              {formData.insurance_mode === 'fleet'
                ? 'Le vehicule sera retire du contrat flotte. Pas de demande de releve d\'informations.'
                : 'Resiliation individuelle avec demande de releve d\'informations obligatoire.'}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Email Download Button */}
      {exitId && insuranceLinks.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-amber-600" />
              <div>
                <h4 className="font-medium text-amber-900">Email de resiliation</h4>
                <p className="text-sm text-amber-700">Telecharger l'email pre-rempli a envoyer a l'assureur</p>
              </div>
            </div>
            <button
              onClick={handleDownloadInsuranceEmail}
              disabled={downloadingEmail}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 text-sm"
            >
              {downloadingEmail ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Telecharger .eml
            </button>
          </div>
        </div>
      )}

      {/* Progress Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{confirmedCount}/{totalContracts}</div>
          <div className="text-sm text-gray-600">Resiliations confirmees</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{refundReceivedCount}/{totalContracts}</div>
          <div className="text-sm text-gray-600">Remboursements recus</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {money(totalRefundAmount, vehicle)}
          </div>
          <div className="text-sm text-gray-600">Total rembourse</div>
        </div>
      </div>

      {/* No Contracts Message */}
      {totalContracts === 0 && (
        <div className="text-center py-8 text-gray-500 border rounded-lg">
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Aucun contrat d'assurance lie a ce vehicule</p>
          <p className="text-xs mt-1">Le vehicule peut etre sorti sans actions assurance</p>
        </div>
      )}

      {/* Contract Cards */}
      {insuranceLinks.map(link => {
        const contractConfig = CONTRACT_TYPES[link.contract_label] || CONTRACT_TYPES.other;

        return (
          <div
            key={link.id}
            className={`border rounded-lg overflow-hidden ${
              link.termination_confirmed ? 'border-green-300 bg-green-50/30' : 'border-gray-200'
            }`}
          >
            {/* Contract Header */}
            <div className={`px-4 py-3 border-b flex items-center justify-between ${
              link.termination_confirmed ? 'bg-green-50' : 'bg-gray-50'
            }`}>
              <div className="flex items-center gap-3">
                <Shield className={`w-5 h-5 text-${contractConfig.color}-600`} />
                <div>
                  <span className="font-medium text-gray-900">{link.contract_name}</span>
                  <span className={`ml-2 text-xs bg-${contractConfig.color}-100 text-${contractConfig.color}-700 px-2 py-0.5 rounded`}>
                    {contractConfig.label}
                  </span>
                </div>
              </div>
              {link.termination_confirmed && (
                <span className="flex items-center gap-1 text-green-600 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Confirme
                </span>
              )}
            </div>

            {/* Contract Body */}
            <div className="p-4 space-y-4">
              {/* Policy Number */}
              {link.policy_number && (
                <p className="text-sm text-gray-600">
                  N de police: <span className="font-mono">{link.policy_number}</span>
                </p>
              )}

              {/* Email Generation Panel */}
              {!link.termination_requested && vehicle?.id && (
                <EmailActionsPanel
                  workflow="insurance_termination"
                  entityType="vehicle"
                  entityId={vehicle.id}
                  entityData={{
                    license_plate: vehicle.plate || vehicle.license_plate,
                    make: vehicle.make,
                    model: vehicle.model,
                    insurance_policy: link.policy_number,
                    contract_name: link.contract_name,
                    insurance_mode: formData.insurance_mode
                  }}
                  compact={true}
                  onEmailGenerated={(info) => {
                    // Auto-check "Demande envoyee" when email is generated
                    updateInsuranceLink(link.id, 'termination_requested', true);
                  }}
                />
              )}

              {/* Termination Status Checkboxes */}
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={link.termination_requested}
                    onChange={(e) => updateInsuranceLink(link.id, 'termination_requested', e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">Demande envoyee</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={link.termination_confirmed}
                    onChange={(e) => updateInsuranceLink(link.id, 'termination_confirmed', e.target.checked)}
                    className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">Confirmation recue</span>
                </label>
              </div>

              {/* Refund Section */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-4 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={link.refund_requested}
                      onChange={(e) => updateInsuranceLink(link.id, 'refund_requested', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Remboursement demande</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={link.refund_received}
                      onChange={(e) => updateInsuranceLink(link.id, 'refund_received', e.target.checked)}
                      className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">Remboursement recu</span>
                  </label>
                </div>

                {link.refund_received && (
                  <div className="flex items-center gap-2">
                    <Euro className="w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={link.refund_amount_eur || ''}
                      onChange={(e) => updateInsuranceLink(link.id, 'refund_amount_eur', e.target.value)}
                      placeholder="Montant rembourse"
                      className="w-40 px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-500">{currencySymbol}</span>
                  </div>
                )}
              </div>

              {/* Claims History (for individual contracts) */}
              {formData.insurance_mode === 'individual' && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Releve d'informations
                  </p>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={link.claims_history_requested}
                        onChange={(e) => updateInsuranceLink(link.id, 'claims_history_requested', e.target.checked)}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Demande envoyee</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={link.claims_history_received}
                        onChange={(e) => updateInsuranceLink(link.id, 'claims_history_received', e.target.checked)}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">Document recu</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <textarea
                  value={link.notes || ''}
                  onChange={(e) => updateInsuranceLink(link.id, 'notes', e.target.value)}
                  placeholder="Notes sur ce contrat..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 resize-none"
                />
              </div>
            </div>
          </div>
        );
      })}

      {/* All Confirmed Banner */}
      {allConfirmed && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <h4 className="font-medium text-green-900">Toutes les resiliations confirmees</h4>
              <p className="text-sm text-green-700 mt-1">
                Vous pouvez finaliser la sortie du vehicule.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pending Warning */}
      {totalContracts > 0 && !allConfirmed && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900">Resiliations en attente</h4>
              <p className="text-sm text-amber-700 mt-1">
                {totalContracts - confirmedCount} contrat(s) en attente de confirmation.
                La sortie ne peut etre finalisee qu'apres confirmation de toutes les resiliations.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StepExitInsurance;
